/**
 * useVoiceCapture -- sovereign browser-side audio capture (voice suite, 2026-07-23).
 *
 * MediaRecorder capture (desktop + mobile) with two modes:
 *   'vad' -- voice-activity auto-stop: RMS noise-floor calibration for the
 *            first ~700ms, speech threshold max(2.5x floor, absolute min),
 *            1.8s of post-speech silence stops the capture, 8s with no
 *            speech at all cancels it.
 *   'ptt' -- push-to-talk: capture runs until stop() is called by the
 *            caller (pointer or key release).
 *
 * Audio lives in memory only: chunks are held in a ref, surfaced once as a
 * Blob to the caller, and dropped. Nothing is logged, persisted, or sent
 * anywhere by this hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type CaptureMode = 'vad' | 'ptt';
export type CaptureState = 'idle' | 'calibrating' | 'listening' | 'processing' | 'unsupported' | 'denied';

// VAD tuning (mirrors the proven governed-voice parameters)
const CALIBRATION_MS = 700;
const THRESHOLD_MULTIPLIER = 2.5;
const ABSOLUTE_MIN_THRESHOLD = 0.012;
const SILENCE_STOP_MS = 1800;
const NO_SPEECH_CANCEL_MS = 8000;
const VAD_TICK_MS = 50;

export interface VoiceCaptureApi {
  state: CaptureState;
  /** Start capturing. Resolves true if capture actually started. */
  start: (mode: CaptureMode) => Promise<boolean>;
  /** Stop capturing (PTT release or manual stop); onAudio fires with the blob. */
  stop: () => void;
  /** Abandon the capture and discard all audio. */
  cancel: () => void;
}

export function useVoiceCapture(onAudio: (audio: Blob) => void): VoiceCaptureApi {
  const [state, setState] = useState<CaptureState>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const vadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardRef = useRef(false);
  const onAudioRef = useRef(onAudio);
  onAudioRef.current = onAudio;

  const teardown = useCallback(() => {
    if (vadTimerRef.current) { clearInterval(vadTimerRef.current); vadTimerRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => undefined); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    recorderRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      setState('processing');
      rec.stop(); // onstop handler surfaces the blob then tears down
    }
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') { rec.stop(); } else { teardown(); setState('idle'); }
  }, [teardown]);

  const start = useCallback(async (mode: CaptureMode): Promise<boolean> => {
    if (recorderRef.current) { return false; } // already capturing; a summon can never restart or confirm
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setState('unsupported');
      return false;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setState('denied');
      return false;
    }
    discardRef.current = false;
    chunksRef.current = [];
    streamRef.current = stream;

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;
    recorder.ondataavailable = (e: BlobEvent) => { if (e.data && e.data.size > 0) { chunksRef.current.push(e.data); } };
    recorder.onstop = () => {
      const mimeType = recorder.mimeType || 'audio/webm';
      const parts = chunksRef.current;
      chunksRef.current = []; // in-memory only; dropped immediately after hand-off
      teardown();
      setState('idle');
      if (!discardRef.current && parts.length > 0) {
        onAudioRef.current(new Blob(parts, { type: mimeType }));
      }
    };
    recorder.start(250);
    setState(mode === 'vad' ? 'calibrating' : 'listening');

    if (mode === 'vad') {
      try {
        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioCtxRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);

        const startedAt = Date.now();
        let floorSum = 0;
        let floorCount = 0;
        let threshold: number | null = null;
        let speechSeen = false;
        let lastSpeechAt = 0;

        vadTimerRef.current = setInterval(() => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const sample = buf[i];
            if (sample === undefined) continue;
            sum += sample * sample;
          }
          const rms = Math.sqrt(sum / buf.length);
          const elapsed = Date.now() - startedAt;

          if (elapsed < CALIBRATION_MS) {
            floorSum += rms;
            floorCount += 1;
            return;
          }
          if (threshold === null) {
            const floor = floorCount > 0 ? floorSum / floorCount : 0;
            threshold = Math.max(THRESHOLD_MULTIPLIER * floor, ABSOLUTE_MIN_THRESHOLD);
            setState('listening');
          }
          if (rms >= threshold) {
            speechSeen = true;
            lastSpeechAt = Date.now();
            return;
          }
          if (speechSeen && Date.now() - lastSpeechAt >= SILENCE_STOP_MS) {
            stop();
            return;
          }
          if (!speechSeen && elapsed >= NO_SPEECH_CANCEL_MS) {
            cancel();
          }
        }, VAD_TICK_MS);
      } catch {
        // No WebAudio (rare) -- capture still works, caller must stop manually.
        setState('listening');
      }
    }
    return true;
  }, [cancel, stop, teardown]);

  useEffect(() => () => { discardRef.current = true; teardown(); }, [teardown]);

  return { state, start, stop, cancel };
}

export default useVoiceCapture;
